import { Box, Stack, Typography } from "@mui/material"
import { LinkType } from "../../graphql/types"
import { Container, Button, SvgIcon, Link, type FC } from ".."

type ListItemProps = {
  title: string
  link: LinkType
}

type CareersListProps = {
  title: string
  subtitle: string
  link: LinkType
  items?: ListItemProps[]
}

const CareersList: FC<CareersListProps> = ({ title, subtitle, link, items }) => {
  const openPositions = Array.isArray(items) && items.length > 0
  return (
    <Container maxWidth={1260} my={{ xs: 15, md: 45 }}>
      <CareersListHeader title={title} subtitle={subtitle} link={link} />
      {openPositions && (
        <Stack
          direction="column"
          borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
        >
          {items?.map((item, i) => (
            <ListItem key={i} index={i} {...item} />
          ))}
        </Stack>
      )}
    </Container>
  )
}

export default CareersList

/** A sub-component for the Careers List header */
const CareersListHeader: FC<Pick<CareersListProps, "title" | "subtitle" | "link">> = ({
  title,
  subtitle,
  link,
}) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      mb={{ xs: 15, md: 27.5 }}
      flexDirection={{ xs: "column", md: "row" }}
    >
      <Stack maxWidth={740} gap={{ xs: 5, md: 10 }}>
        <Typography variant="body.lg" component="h3">
          {subtitle}
        </Typography>
        <Typography variant="heading.2xl" component="h2">
          {title}
        </Typography>
      </Stack>
      <Box display="flex" alignItems="flex-end">
        <CareersListCTA link={link} />
      </Box>
    </Box>
  )
}

/** A sub-component for the Careers List CTA */
const CareersListCTA: FC<Pick<CareersListProps, "link">> = ({ link }) => (
  <Box mt={{ xs: 10, md: 0 }} width={{ xs: "100%", md: "auto" }}>
    <Button
      variant="default"
      label={link.title}
      href={link.uri}
      sx={(theme) => ({
        [theme.breakpoints.down("md")]: {
          width: "100%",
        },
      })}
    />
  </Box>
)

/** A sub-component for the Careers List item */
const ListItem: FC<ListItemProps & { index: number }> = ({ title, link, index }) => {
  return (
    <Stack
      justifyContent="space-between"
      direction="row"
      position="relative"
      sx={(theme) => ({
        px: { xs: 3, md: 5 },
        py: { xs: 6, md: 7 },
        padding: theme.spacing(7, 5),
        borderBottom: `1px solid ${theme.palette["neutral.200"].main}`,
      })}
    >
      <Link type="overlay" href={link.uri} title={title} />
      <Box>
        <Stack direction="row" gap={{ xs: 3, md: 10 }} alignItems="center">
          <Typography variant="body.sm">{index < 9 ? `0${index + 1}` : index + 1}</Typography>
          <Typography variant="body.lg" component="h2">{title}</Typography>
        </Stack>
      </Box>
      <Stack direction="row" alignItems="center" columnGap={3} ml="auto">
        <Typography variant="link.xs" display={{ xs: "none", md: "block" }}>
          Explore
        </Typography>
        <SvgIcon variant="arrow" sx={{ transform: "rotate(180deg)" }} size={16} />
      </Stack>
    </Stack>
  )
}
